<div
  class="function-machine"
  [class.function-machine--horizontal]="layout() === 'horizontal'"
  [class.function-machine--vertical]="layout() === 'vertical'"
  [class.function-machine--inverse]="isInverse()"
  role="figure"
  [attr.aria-label]="'Function machine with ' + visibleBlocks().length + ' steps'"
>
  @for (item of visibleBlocks(); track item.block.id; let i = $index) {
    <!-- Arrow before block (except first) -->
    @if (i > 0) {
      <div
        class="fm-arrow"
        [class.fm-arrow--animated]="isHighlighted() && isElementAnimated('arrow-' + (i - 1))"
        [style.--arrow-color]="getActiveColor()"
        role="presentation"
        aria-hidden="true"
      >
        <svg class="fm-arrow__svg" viewBox="0 0 40 24">
          <line x1="0" y1="12" x2="30" y2="12" stroke="currentColor" stroke-width="2" />
          <polygon points="30,6 40,12 30,18" fill="currentColor" />
        </svg>
      </div>
    }

    <!-- Block -->
    @switch (item.block.type) {
      @case ('value') {
        <div
          class="fm-block fm-block--value"
          [class.fm-block--error]="errors().has(item.block.id)"
          [class.fm-block--value--highlighted]="isHighlighted() && isElementAnimated('block-' + i)"
          [style.--highlight-color]="getActiveColor()"
          role="group"
          [attr.aria-label]="getValueAriaLabel(item.block, item.isFirst, item.isLast)"
        >
          @if (shouldShowLabel(item.block, item.isFirst, item.isLast)) {
            <span class="fm-block__label">{{ getBlockLabel(item.block, item.isFirst, item.isLast) }}</span>
          }

          @if (isEditable(item.block)) {
            <input
              type="number"
              class="fm-block__input"
              [value]="getInputValue(item.block)"
              (input)="onInputChange(item.block.id, $event)"
              (keydown.enter)="onRunAll()"
              [attr.aria-label]="'Enter ' + (getBlockLabel(item.block, item.isFirst, item.isLast) ?? 'value')"
            />
          } @else {
            <span
              class="fm-block__value"
              [class.fm-block__value--error]="errors().has(item.block.id)"
              [class.fm-block__value--pending]="getDisplayValue(item.block) === '?'"
            >
              {{ getDisplayValue(item.block) }}
            </span>
          }
        </div>
      }

      @case ('function') {
        <div class="fm-block-wrapper">
          <div
            class="fm-block fm-block--function"
            [class.fm-block--function--highlighted]="isHighlighted() && isElementAnimated('block-' + i)"
            [style.--block-highlight-bg]="'var(--' + item.block.color + '-100)'"
            [style.--block-highlight-text]="'var(--' + item.block.color + '-600)'"
            role="group"
            [attr.aria-label]="'Function: ' + item.block.expression"
          >
            <div
              class="fm-block__gear"
              [style.color]="'var(--' + item.block.color + '-500)'"
              [class.fm-block__gear--spinning]="isBlockRunning(item.block.id)"
            >
              <svg width="16" height="16" viewBox="0 0 512 512" fill="currentColor" aria-hidden="true">
                <path
                  d="M495.9 166.6c3.2 8.7 .5 18.4-6.4 24.6l-43.3 39.4c1.1 8.3 1.7 16.8 1.7 25.4s-.6 17.1-1.7 25.4l43.3 39.4c6.9 6.2 9.6 15.9 6.4 24.6c-4.4 11.9-9.7 23.3-15.8 34.3l-4.7 8.1c-6.6 11-14 21.4-22.1 31.2c-5.9 7.2-15.7 9.6-24.5 6.8l-55.7-17.7c-13.4 10.3-28.2 18.9-44 25.4l-12.5 57.1c-2 9.1-9 16.3-18.2 17.8c-13.8 2.3-28 3.5-42.5 3.5s-28.7-1.2-42.5-3.5c-9.2-1.5-16.2-8.7-18.2-17.8l-12.5-57.1c-15.8-6.5-30.6-15.1-44-25.4L83.1 425.9c-8.8 2.8-18.6 .3-24.5-6.8c-8.1-9.8-15.5-20.2-22.1-31.2l-4.7-8.1c-6.1-11-11.4-22.4-15.8-34.3c-3.2-8.7-.5-18.4 6.4-24.6l43.3-39.4C64.6 273.1 64 264.6 64 256s.6-17.1 1.7-25.4L22.4 191.2c-6.9-6.2-9.6-15.9-6.4-24.6c4.4-11.9 9.7-23.3 15.8-34.3l4.7-8.1c6.6-11 14-21.4 22.1-31.2c5.9-7.2 15.7-9.6 24.5-6.8l55.7 17.7c13.4-10.3 28.2-18.9 44-25.4l12.5-57.1c2-9.1 9-16.3 18.2-17.8C227.3 1.2 241.5 0 256 0s28.7 1.2 42.5 3.5c9.2 1.5 16.2 8.7 18.2 17.8l12.5 57.1c15.8 6.5 30.6 15.1 44 25.4l55.7-17.7c8.8-2.8 18.6-.3 24.5 6.8c8.1 9.8 15.5 20.2 22.1 31.2l4.7 8.1c6.1 11 11.4 22.4 15.8 34.3zM256 336a80 80 0 1 0 0-160 80 80 0 1 0 0 160z"
                />
              </svg>
            </div>
            <span class="fm-block__expression" [dojoRichText]="getExpressionDisplay(item.block)"></span>
          </div>

          @if (showPerBlockRun()) {
            <button
              class="fm-run-btn"
              [style.--btn-color]="'var(--' + item.block.color + '-500)'"
              [style.--btn-hover]="'var(--' + item.block.color + '-600)'"
              (click)="onRunUpTo(i)"
              [disabled]="runningState() === 'running'"
              [attr.aria-label]="'Run function ' + (item.block.name ?? item.block.expression)"
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 384 512"
                fill="currentColor"
                class="fm-run-btn__icon"
                aria-hidden="true"
              >
                <path
                  d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z"
                />
              </svg>
              Run
            </button>
          }
        </div>
      }

      @case ('transform') {
        <div class="fm-block-wrapper">
          <div
            class="fm-block fm-block--transform"
            [class.fm-block--transform--filled]="!!$any(item.block).color"
            [class.fm-block--transform--highlighted]="isHighlighted() && isElementAnimated('block-' + i)"
            [style.--transform-fill]="$any(item.block).color ? 'var(--' + $any(item.block).color + '-500)' : null"
            [style.--block-highlight-bg]="'var(--' + getTransformColorName($any(item.block)) + '-100)'"
            [style.--block-highlight-text]="'var(--' + getTransformColorName($any(item.block)) + '-600)'"
            role="group"
            [attr.aria-label]="'Transform: ' + item.block.operation"
          >
            <span class="fm-block__operation" [dojoRichText]="getOperationDisplay($any(item.block))"></span>
          </div>

          @if (showPerBlockRun()) {
            <button
              class="fm-run-btn fm-run-btn--neutral"
              (click)="onRunUpTo(i)"
              [disabled]="runningState() === 'running'"
              [attr.aria-label]="'Run transform ' + item.block.operation"
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 384 512"
                fill="currentColor"
                class="fm-run-btn__icon"
                aria-hidden="true"
              >
                <path
                  d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z"
                />
              </svg>
              Run
            </button>
          }
        </div>
      }
    }
  }
</div>

<!-- Controls: Invert toggle + Run button -->
@if (hasInverse() && showGlobalRun()) {
  <div class="fm-controls">
    <button
      class="fm-invert-btn"
      [class.fm-invert-btn--active]="isInverseActive()"
      [style.--invert-color]="'var(--' + getInverseColor() + '-500)'"
      [style.--invert-hover]="'var(--' + getInverseColor() + '-600)'"
      [style.--invert-bg]="'var(--' + getInverseColor() + '-50)'"
      (click)="onToggleInverse()"
      [disabled]="runningState() === 'running'"
      aria-label="Toggle inverse mode"
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 512 512"
        fill="currentColor"
        class="fm-invert-btn__icon"
        aria-hidden="true"
      >
        <path
          d="M32 96l320 0V32c0-12.9 7.8-24.6 19.8-29.6s25.7-2.2 34.9 6.9l96 96c6 6 9.4 14.1 9.4 22.6s-3.4 16.6-9.4 22.6l-96 96c-9.2 9.2-22.9 11.9-34.9 6.9s-19.8-16.6-19.8-29.6V160L32 160c-17.7 0-32-14.3-32-32s14.3-32 32-32zM480 352c17.7 0 32 14.3 32 32s-14.3 32-32 32l-320 0v64c0 12.9-7.8 24.6-19.8 29.6s-25.7 2.2-34.9-6.9l-96-96c-6-6-9.4-14.1-9.4-22.6s3.4-16.6 9.4-22.6l96-96c9.2-9.2 22.9-11.9 34.9-6.9s19.8 16.6 19.8 29.6v64l320 0z"
        />
      </svg>
      Inverse
    </button>

    <button
      class="fm-run-btn fm-run-btn--global"
      [style.--btn-color]="'var(--' + getPrimaryColor() + '-500)'"
      [style.--btn-hover]="'var(--' + getPrimaryColor() + '-600)'"
      (click)="onRunAll()"
      [disabled]="runningState() === 'running'"
      aria-label="Run function machine"
    >
      <svg width="12" height="12" viewBox="0 0 384 512" fill="currentColor" class="fm-run-btn__icon" aria-hidden="true">
        <path
          d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z"
        />
      </svg>
      Run
    </button>
  </div>
}

<!-- Global run button (no inverse) -->
@if (!hasInverse() && showGlobalRun()) {
  <div class="fm-global-run">
    <button
      class="fm-run-btn fm-run-btn--global"
      [style.--btn-color]="'var(--' + getPrimaryColor() + '-500)'"
      [style.--btn-hover]="'var(--' + getPrimaryColor() + '-600)'"
      (click)="onRunAll()"
      [disabled]="runningState() === 'running'"
      aria-label="Run function machine"
    >
      <svg width="12" height="12" viewBox="0 0 384 512" fill="currentColor" class="fm-run-btn__icon" aria-hidden="true">
        <path
          d="M73 39c-14.8-9.1-33.4-9.4-48.5-.9S0 62.6 0 80V432c0 17.4 9.4 33.4 24.5 41.9s33.7 8.1 48.5-.9L361 297c14.3-8.7 23-24.2 23-41s-8.7-32.2-23-41L73 39z"
        />
      </svg>
      Run
    </button>
  </div>
}
